import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search, Download, Play, RotateCcw, ShoppingBag, Truck, Shirt, AlertTriangle, Receipt,
  ArrowRight, CalendarRange, Users2, Package, FileSpreadsheet, BarChart3,
} from 'lucide-react';
import { fetchReportCatalogue, runReport, listCustomers, listItems } from '../api/resources';
import { downloadCSV, downloadExcel } from '../utils/format';
import { useToast } from '../context/ToastContext';
import Spinner from '../components/ui/Spinner';
import EmptyState from '../components/ui/EmptyState';
import DateInput from '../components/ui/DateInput';
import SearchableSelect from '../components/ui/SearchableSelect';
import Pagination from '../components/ui/Pagination';
import PageHeader from '../components/ui/PageHeader';

const PER_PAGE = 25;

const CATEGORY = {
  Booking: { icon: ShoppingBag, chip: 'bg-info-50 text-info-700', dot: 'bg-info-500' },
  Delivery: { icon: Truck, chip: 'bg-emerald-50 text-emerald-700', dot: 'bg-success-500' },
  Trial: { icon: Shirt, chip: 'bg-warning-50 text-warning-700', dot: 'bg-warning-500' },
  'Not Ready': { icon: AlertTriangle, chip: 'bg-rose-50 text-rose-700', dot: 'bg-danger-500' },
  Invoice: { icon: Receipt, chip: 'bg-violet-50 text-violet-700', dot: 'bg-violet-500' },
};

const DESCRIPTIONS = {
  'Booking Item Wise': 'Ordered quantity & amount grouped by item',
  'Booking Invoice Wise': 'All bookings with amounts, GST & balance',
  'Booking Customer Wise': 'Orders & total spend per customer',
  'Delivery Date & Item Wise': 'Deliveries by date with item-wise quantity & sales',
  'Delivery Date & Sale Wise': 'Deliveries by date with order count & sales',
  'Delivery Item Wise': 'Delivered quantity & amount grouped by item',
  'Delivery Invoice Wise': 'Delivered invoices with amounts & status',
  'Delivery Employee Wise': 'Deliveries by salesman — quantity & sales',
  'Delivery Sale & Item Wise': 'Delivery sales by date & item',
  'Delivery Sale Wise': 'Delivery sales by date',
  'Trial Date & Item Wise': 'Trials by date with item-wise quantity',
  'Trial Item Wise': 'Trial quantity & amount grouped by item',
  'Trial Invoice Wise': 'Invoices with upcoming trial dates',
  'Not Ready Item Wise': 'Items on orders not yet delivered',
  'Not Ready Delivery Date Wise': 'Pending deliveries grouped by date',
  'Not Ready Trial Date Wise': 'Pending trials grouped by date',
  'Invoice Detail': 'Full invoice register with payments & balance',
};

// An item-wise report counts by one date; the orders behind a row are pulled
// with the same one, or the two lists would not add up.
const DRILL = (name) => {
  if (!name) return null;
  if (name.startsWith('Delivery')) return { dateField: 'deliveryDate', label: 'Delivery date' };
  if (name.startsWith('Trial')) return { dateField: 'trialDate', label: 'Trial date' };
  if (name.startsWith('Not Ready')) return { dateField: 'deliveryDate', label: 'Delivery date', openOnly: true };
  return { dateField: 'orderDate', label: 'Order date' };
};

export default function Reports() {
  const { error } = useToast();
  const [selected, setSelected] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [itemId, setItemId] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [query, setQuery] = useState('');
  // The item row the user clicked, if any — its orders open over the report.
  const [drill, setDrill] = useState(null);

  const { data: catData } = useQuery({ queryKey: ['report-catalogue'], queryFn: fetchReportCatalogue });
  const reports = catData?.reports || [];

  const { data: custData } = useQuery({ queryKey: ['report-customers'], queryFn: () => listCustomers({ perPage: 200 }) });
  const { data: itemData } = useQuery({ queryKey: ['report-items'], queryFn: () => listItems({ perPage: 200 }) });

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const g = {};
    for (const r of reports) {
      if (q && !r.name.toLowerCase().includes(q)) continue;
      (g[r.category] = g[r.category] || []).push(r);
    }
    return g;
  }, [reports, query]);

  const isItemReport = selected?.name?.includes('Item');

  const run = async () => {
    if (!selected) return error('Select a report to run.');
    setRunning(true);
    setPage(1);
    try {
      const res = await runReport({
        name: selected.name,
        from: from || undefined,
        to: to || undefined,
        customerId: customerId || undefined,
        itemId: itemId || undefined,
      });
      setResult(res);
    } catch (x) {
      error(x.response?.data?.message || 'Could not run report.');
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

const exportRowsOf = (res) => res.rows.map((r) => {
    const obj = {};
    res.columns.forEach((c, i) => {
      const candidates = res.keys?.[i] || [c];
      const k = candidates.find((kk) => r[kk] !== undefined) || candidates[0];
      obj[c] = r[k];
    });
    return obj;
  });

  const exportCSV = () => {
    if (!result) return;
    downloadCSV(`${selected.name.replace(/\s+/g, '-')}.csv`, result.columns, exportRowsOf(result));
  };

  const exportXls = () => {
    if (!result) return;
    downloadExcel(`${selected.name.replace(/\s+/g, '-')}.xls`, result.columns, exportRowsOf(result));
  };

  const reset = () => { setFrom(''); setTo(''); setCustomerId(''); setItemId(''); setResult(null); };

  const paged = result?.rows?.slice((page - 1) * PER_PAGE, page * PER_PAGE) || [];

  const cell = (r, i) => {
    const candidates = result.keys?.[i] || [result.columns[i]];
    const k = candidates.find((kk) => r[kk] !== undefined) || candidates[0];
    const v = r[k];
    const isMoney = /amount|sales|total|received|due|gst/i.test(result.columns[i]);
    if (isMoney && typeof v === 'number') return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(v);
    if (result.columns[i] === 'Status') return <StatusPill status={v} />;
    return v;
  };

  const totalGroups = Object.keys(groups).length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BarChart3}
        title="Reports"
        subtitle="Run booking, delivery, trial and invoice reports"
        actions={
          selected && result ? (
            <button className="btn-accent" onClick={exportCSV}><Download size={16} /> Export CSV</button>
          ) : undefined
        }
      />

      <div className="grid gap-6 lg:grid-cols-5">
        {/* ---- Catalogue ---- */}
        <div className="lg:col-span-2 xl:col-span-2">
          <div className="card h-fit overflow-hidden">
            <div className="grad-brand px-4 py-3.5 text-white">
              <p className="font-display text-base font-semibold">Report Catalogue</p>
              <p className="text-xs text-white/70">{reports.length} reports · {totalGroups} categories</p>
            </div>
            <div className="border-b border-line p-3">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
                <input
                  className="input !py-2 pl-9"
                  placeholder="Search reports…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="sidebar-scroll max-h-[calc(100vh-20rem)] space-y-4 overflow-y-auto p-3">
              {Object.keys(groups).length === 0 && (
                <div className="py-10 text-center">
                  <FileSpreadsheet size={26} className="mx-auto mb-2 text-content-subtle" />
                  <p className="text-sm text-content-subtle">No reports match “{query}”.</p>
                </div>
              )}
              {Object.keys(groups).map((cat) => {
                const meta = CATEGORY[cat] || { chip: 'bg-surface-hover text-content-muted', dot: 'bg-content-subtle' };
                const Icon = meta.icon || FileSpreadsheet;
                return (
                  <div key={cat}>
                    <div className="mb-1.5 flex items-center gap-2 px-1">
                      <span className={`flex h-6 w-6 items-center justify-center rounded-lg ${meta.chip}`}>
                        <Icon size={13} />
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-widest text-content-subtle">{cat}</span>
                      <span className="ml-auto rounded-full bg-surface-hover px-1.5 text-[10px] font-bold text-content-subtle">{groups[cat].length}</span>
                    </div>
                    <div className="space-y-1.5">
                      {groups[cat].map((r) => {
                        const active = selected?.name === r.name;
                        return (
                          <button
                            key={r.name}
                            onClick={() => setSelected(r)}
                            className={`group flex w-full items-center gap-2.5 rounded-xl border p-2.5 text-left text-sm transition-all ${
                              active
                                ? 'border-brand-600 bg-gradient-to-r from-brand-600 to-brand-700 text-white shadow-sm'
                                : 'border-transparent text-content-muted hover:border-brand-200 hover:bg-brand-50 hover:text-brand-800'
                            }`}
                          >
                            <span
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
                                active ? 'bg-surface/15 text-white' : meta.chip
                              }`}
                            >
                              <Icon size={15} />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-semibold leading-tight">{r.name}</span>
                              <span className={`block truncate text-[11px] ${active ? 'text-white/70' : 'text-content-subtle'}`}>
                                {DESCRIPTIONS[r.name] || r.category}
                              </span>
                            </span>
                            <ArrowRight
                              size={14}
                              className={`shrink-0 transition-transform group-hover:translate-x-0.5 ${active ? 'text-white/80' : 'text-content-subtle'}`}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ---- Config + results ---- */}
        <div className="space-y-5 lg:col-span-3">
          {!selected ? (
            /* Hero: nothing selected */
            <div className="card flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="grad-brand mb-4 flex h-16 w-16 items-center justify-center rounded-2xl text-white shadow-lg">
                <FileSpreadsheet size={28} />
              </div>
              <h3 className="font-display text-xl font-semibold text-content">Pick a report to get started</h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-content-subtle">
                Choose a report from the catalogue on the left, set your date range and filters, then run it to view the results here.
              </p>
              <div className="mt-6 grid w-full max-w-lg grid-cols-2 gap-3 sm:grid-cols-3">
                {Object.keys(CATEGORY).slice(0, 4).map((cat) => {
                  const m = CATEGORY[cat];
                  const I = m.icon;
                  return (
                    <div key={cat} className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${m.chip}`}>
                      <I size={15} /> {cat}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Config panel */
            <div className="card">
              <div className="grad-brand rounded-t-xl px-5 py-4 text-white">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface/15">
                      {(() => {
                        const Icon = CATEGORY[selected.category]?.icon || FileSpreadsheet;
                        return <Icon size={18} />;
                      })()}
                    </span>
                    <div>
                      <p className="font-display text-lg font-semibold leading-tight">{selected.name}</p>
                      <p className="text-xs text-white/70">{DESCRIPTIONS[selected.name] || selected.category}</p>
                    </div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${CATEGORY[selected.category]?.chip || 'bg-surface/20 text-white'}`}>
                    {selected.category}
                  </span>
                </div>
              </div>

              <div className="p-5">
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <label className="label flex items-center gap-1"><CalendarRange size={13} /> From Date</label>
                    <DateInput value={from} onChange={setFrom} />
                  </div>
                  <div>
                    <label className="label flex items-center gap-1"><CalendarRange size={13} /> To Date</label>
                    <DateInput value={to} onChange={setTo} />
                  </div>
                  <div>
                    <label className="label flex items-center gap-1"><Users2 size={13} /> Customer</label>
                    <SearchableSelect
                      options={(custData?.customers || []).map((c) => ({ id: c.id, label: c.name, sub: c.mobile }))}
                      value={customerId}
                      onSelect={(o) => setCustomerId(String(o.id))}
                      placeholder="All customers…"
                    />
                  </div>
                  {isItemReport && (
                    <div>
                      <label className="label flex items-center gap-1"><Package size={13} /> Item</label>
                      <SearchableSelect
                        options={(itemData?.tailoringItems || []).map((i) => ({ id: i.id, label: i.name }))}
                        value={itemId}
                        onSelect={(o) => setItemId(String(o.id))}
                        placeholder="All items…"
                      />
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-4">
                  <button className="btn-primary" onClick={run} disabled={running}>
                    <Play size={15} /> {running ? 'Running…' : 'Run Report'}
                  </button>
                  <button className="btn-outline" onClick={reset}><RotateCcw size={15} /> Reset</button>
                </div>
              </div>
            </div>
          )}

          {/* ---- Results ---- */}
          {running && (
            <div className="card p-6"><Spinner label="Running report…" /></div>
          )}
          {result && !running && (
            <div className="card overflow-hidden">
              {result.rows.length === 0 ? (
                <div className="px-5 py-10">
                  <EmptyState
                    icon="📊"
                    title="No data in range"
                    hint="Try widening the date range or clearing the filters."
                  />
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-5 py-3">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet size={16} className="text-brand-600" />
                      <p className="text-sm font-semibold text-content">{result.name}</p>
                      <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-bold text-brand-700">{result.total} rows</span>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      {result.rows.some((r) => r.itemName) && DRILL(result.name) && (
                        <span className="text-2xs text-content-subtle">Click an item to see its orders</span>
                      )}
                      <button className="btn-outline !py-1.5 text-xs" onClick={exportCSV}><Download size={13} /> CSV</button>
                      <button className="btn-accent !py-1.5 text-xs" onClick={exportXls}><Download size={13} /> Excel</button>
                    </div>
                  </div>
                  <div className="table-scroll max-h-[34rem] overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10">
                        <tr>
                          {result.columns.map((c) => (
                            <th key={c} className="th">{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paged.map((r, ri) => {
                          const openable = !!(r.itemName && DRILL(result.name));
                          return (
                          <tr
                            key={ri}
                            className={`border-b border-line transition-colors hover:bg-brand-50/40 ${ri % 2 ? 'bg-surface-hover' : ''} ${openable ? 'cursor-pointer' : ''}`}
                            title={openable ? `Show the orders for ${r.itemName}` : undefined}
                            onClick={openable ? () => setDrill({ itemName: r.itemName, report: result.name }) : undefined}
                          >
                            {result.columns.map((c, ci) => (
                              <td key={c} className={`td whitespace-nowrap ${/amount|sales|total|received|due|gst|qty|quantity|orders/i.test(result.columns[ci]) ? 'text-right' : ''}`}>
                                {ci === 0 && openable
                                  ? <span className="font-semibold text-brand-700 underline decoration-dotted underline-offset-2">{cell(r, ci)}</span>
                                  : cell(r, ci)}
                              </td>
                            ))}
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Pagination page={page} perPage={PER_PAGE} total={result.total} onPage={setPage} onPerPage={() => setPage(1)} />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {drill && (
        <ItemOrders
          itemName={drill.itemName}
          report={drill.report}
          from={from}
          to={to}
          customerId={customerId}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

/**
 * The orders behind one line of an item-wise report — same date range, same
 * date field, same customer filter, so the quantities match what was clicked.
 */
function ItemOrders({ itemName, report, from, to, customerId, onClose }) {
  const drill = DRILL(report);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['item-orders', itemName, report, from, to, customerId],
    queryFn: () => runReport({
      name: 'Item Orders',
      itemName,
      dateField: drill.dateField,
      openOnly: drill.openOnly ? 1 : undefined,
      from: from || undefined,
      to: to || undefined,
      customerId: customerId || undefined,
    }),
  });

  const rows = data?.rows || [];
  const pieces = rows.reduce((a, r) => a + Number(r.qty || 0), 0);
  const amount = rows.reduce((a, r) => a + Number(r.amount || 0), 0);
  const money = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(v || 0);
  const period = from || to ? `${from || '…'} to ${to || '…'}` : 'all dates';

  const sheetRows = () => rows.map((r) => {
    const o = {};
    data.columns.forEach((c, i) => {
      const k = (data.keys?.[i] || [c]).find((kk) => r[kk] !== undefined) || c;
      o[c] = r[k];
    });
    return o;
  });
  const base = `${itemName.replace(/\s+/g, '-')}-orders`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="card flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-5 py-3">
          <Package size={16} className="text-brand-600" />
          <h3 className="text-sm font-bold text-content">{itemName}</h3>
          <span className="text-2xs text-content-subtle">
            {drill.label} · {period}{drill.openOnly ? ' · not delivered yet' : ''}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {rows.length > 0 && (
              <>
                <button className="btn-outline btn-sm" onClick={() => downloadCSV(`${base}.csv`, data.columns, sheetRows())}>
                  <Download size={13} /> CSV
                </button>
                <button className="btn-outline btn-sm" onClick={() => downloadExcel(`${base}.xls`, data.columns, sheetRows())}>
                  <Download size={13} /> Excel
                </button>
              </>
            )}
            <button className="btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>

        {isLoading ? (
          <div className="p-8"><Spinner label="Finding the orders…" /></div>
        ) : isError ? (
          <p className="p-8 text-center text-sm text-danger-600">Could not load these orders.</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-sm italic text-content-subtle">No orders for {itemName} in this period.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-6 gap-y-1 border-b border-line bg-surface-sunken px-5 py-2 text-xs">
              <span><b className="tabular-nums">{rows.length}</b> order line(s)</span>
              <span><b className="tabular-nums">{pieces}</b> piece(s)</span>
              <span><b className="tabular-nums">{money(amount)}</b> total</span>
            </div>
            <div className="table-scroll flex-1 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <tr>
                    {data.columns.map((c) => <th key={c} className="th">{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, ri) => (
                    <tr key={ri} className={`border-b border-line ${ri % 2 ? 'bg-surface-hover' : ''}`}>
                      {data.columns.map((c, ci) => {
                        const k = (data.keys?.[ci] || [c]).find((kk) => r[kk] !== undefined) || c;
                        const v = r[k];
                        const right = /amount|rate|quantity|qty/i.test(c);
                        return (
                          <td key={c} className={`td whitespace-nowrap ${right ? 'text-right' : ''}`}>
                            {c === 'Status' ? <StatusPill status={v} />
                              : /amount|rate/i.test(c) && typeof v === 'number' ? money(v)
                                : v}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    BOOKED: 'bg-info-50 text-info-700',
    IN_PROGRESS: 'bg-warning-50 text-warning-700',
    DELIVERED: 'bg-success-50 text-success-700',
    CANCELLED: 'bg-danger-50 text-danger-700',
  };
  return <span className={`badge ${map[status] || 'bg-surface-hover text-content-muted'}`}>{(status || '').replace(/_/g, ' ')}</span>;
}
